package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MonthCalendar
import br.com.vvrefeicoes.portal.domain.model.MonthPerformance
import br.com.vvrefeicoes.portal.domain.model.RatingCounts
import br.com.vvrefeicoes.portal.domain.model.SatisfactionMonth
import br.com.vvrefeicoes.portal.domain.model.UnitSatisfaction
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.BadgePercent
import com.composables.icons.lucide.CircleAlert
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.ThumbsUp
import com.composables.icons.lucide.TrendingDown
import com.composables.icons.lucide.TrendingUp
import com.composables.icons.lucide.Vote
import io.github.koalaplot.core.line.LinePlot
import io.github.koalaplot.core.style.LineStyle
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.Point
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_satisfaction_col_bad
import portalvv.shared.generated.resources.operational_satisfaction_col_excellent
import portalvv.shared.generated.resources.operational_satisfaction_col_good
import portalvv.shared.generated.resources.operational_satisfaction_col_location
import portalvv.shared.generated.resources.operational_satisfaction_col_regular
import portalvv.shared.generated.resources.operational_satisfaction_col_satisfaction
import portalvv.shared.generated.resources.operational_satisfaction_col_total_votes
import portalvv.shared.generated.resources.operational_satisfaction_empty
import portalvv.shared.generated.resources.operational_satisfaction_evolution_satisfaction
import portalvv.shared.generated.resources.operational_satisfaction_evolution_votes
import portalvv.shared.generated.resources.operational_satisfaction_legend_satisfaction
import portalvv.shared.generated.resources.operational_satisfaction_legend_votes
import portalvv.shared.generated.resources.operational_satisfaction_overall
import portalvv.shared.generated.resources.operational_satisfaction_performance_title
import portalvv.shared.generated.resources.operational_satisfaction_units_title
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.roundToLong

private val KPI_HEIGHT = 96.dp
private val LINE_CHART_HEIGHT = 240.dp
private val DONUT_SIZE = 160.dp

/** Acima deste % o rótulo do ponto iria para fora da área do plot e seria cortado; então vai abaixo dele. */
private const val PERCENT_LABEL_ABOVE_MAX = 80f
private const val SATISFACTION_MIN_PERCENT = 85.0

private val PERFORMANCE_THREE_COLUMNS_MIN_WIDTH = 900.dp
private val PERFORMANCE_TWO_COLUMNS_MIN_WIDTH = 600.dp

private const val W_LABEL = 1.7f
private const val W_SMALL = 0.9f
private const val W_MEDIUM = 1.1f

@Composable
fun SatisfactionDashboardContent() {
    val controller = rememberSatisfactionDashboardController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    when {
        controller.loading ->
            Box(
                modifier = Modifier.fillMaxWidth().height(200.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        controller.isEmpty ->
            EmptyState(message = stringResource(Res.string.operational_satisfaction_empty))
        else -> {
            SatisfactionKpis(
                totalVotes = controller.totalVotes,
                satisfactionPercent = controller.satisfactionPercent,
                counts = controller.counts,
            )
            SatisfactionLines(evolution = controller.evolution)
            PerformanceCard(performance = controller.performance)
            UnitsCard(units = controller.units)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun SatisfactionKpis(
    totalVotes: Int,
    satisfactionPercent: Double,
    counts: RatingCounts,
) {
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        maxItemsInEachRow = 6,
    ) {
        KpiTile(
            icon = Lucide.Vote,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_satisfaction_col_total_votes),
            value = totalVotes.toString(),
        )
        KpiTile(
            icon = Lucide.BadgePercent,
            iconColor = AppPalette.Info500,
            iconContainerColor = AppPalette.Info100,
            label = stringResource(Res.string.operational_satisfaction_overall),
            value = percentText(satisfactionPercent),
            valueColor = if (satisfactionPercent >= SATISFACTION_MIN_PERCENT) AppPalette.Success500 else AppPalette.Error500,
        )
        KpiTile(
            icon = Lucide.ThumbsUp,
            iconColor = AppPalette.Success500,
            iconContainerColor = AppPalette.Success100,
            label = stringResource(Res.string.operational_satisfaction_col_excellent),
            value = counts.excellent.toString(),
        )
        KpiTile(
            icon = Lucide.TrendingUp,
            iconColor = AppPalette.Accent500,
            iconContainerColor = AppPalette.Accent100,
            label = stringResource(Res.string.operational_satisfaction_col_good),
            value = counts.good.toString(),
        )
        KpiTile(
            icon = Lucide.CircleAlert,
            iconColor = AppPalette.Warning500,
            iconContainerColor = AppPalette.Warning100,
            label = stringResource(Res.string.operational_satisfaction_col_regular),
            value = counts.regular.toString(),
        )
        KpiTile(
            icon = Lucide.TrendingDown,
            iconColor = AppPalette.Error500,
            iconContainerColor = AppPalette.Error100,
            label = stringResource(Res.string.operational_satisfaction_col_bad),
            value = counts.bad.toString(),
        )
    }
}

@Composable
private fun FlowRowScope.KpiTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    valueColor: Color = MaterialTheme.colorScheme.onSurface,
) {
    Surface(
        modifier = Modifier.weight(1f).height(KPI_HEIGHT),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = valueColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun SatisfactionLines(evolution: List<SatisfactionMonth>) {
    val labels = evolution.map { MonthCalendar.shortLabel(it.period) }
    LineCard(
        title = stringResource(Res.string.operational_satisfaction_evolution_votes),
        legend = stringResource(Res.string.operational_satisfaction_legend_votes),
        labels = labels,
        values = evolution.map { it.votes.toFloat() },
        percent = false,
    )
    LineCard(
        title = stringResource(Res.string.operational_satisfaction_evolution_satisfaction),
        legend = stringResource(Res.string.operational_satisfaction_legend_satisfaction),
        labels = labels,
        values = evolution.map { it.satisfactionPercent.toFloat() },
        percent = true,
    )
}

@Composable
private fun LineCard(
    title: String,
    legend: String,
    labels: List<String>,
    values: List<Float>,
    percent: Boolean,
) {
    ChartCard(title = title, modifier = Modifier.fillMaxWidth()) {
        SeriesLegend(entries = listOf(LegendEntry(AppPalette.Primary500, legend)))
        if (labels.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            val points = labels.mapIndexed { index, label -> Point(label, values[index]) }
            val maxValue = values.maxOrNull()?.coerceAtLeast(1f) ?: 1f
            val yAxis = if (percent) rememberPercentAxis() else rememberValueAxis(maxValue)
            XYGraph(
                gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                xAxisModel = CategoryAxisModel(labels),
                yAxisModel = yAxis,
                xAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                yAxisContent = rememberAxisContent(labels = { value -> Text(axisValue(value, percent), style = MaterialTheme.typography.labelSmall) }),
                modifier = Modifier.fillMaxWidth().height(LINE_CHART_HEIGHT),
            ) {
                LinePlot(
                    data = points,
                    lineStyle = LineStyle(brush = SolidColor(AppPalette.Primary500), strokeWidth = 2.dp),
                    symbol = { point ->
                        val above = !percent || point.y < PERCENT_LABEL_ABOVE_MAX
                        HoverTooltip(text = "${point.x}: ${axisValue(point.y, percent)}") {
                            LinePointLabel(axisValue(point.y, percent), AppPalette.Primary500, above = above)
                        }
                    },
                )
            }
        }
    }
}

@Composable
private fun PerformanceCard(performance: List<MonthPerformance>) {
    ChartCard(
        title = stringResource(Res.string.operational_satisfaction_performance_title),
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (performance.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                val columns =
                    when {
                        maxWidth >= PERFORMANCE_THREE_COLUMNS_MIN_WIDTH -> 3
                        maxWidth >= PERFORMANCE_TWO_COLUMNS_MIN_WIDTH -> 2
                        else -> 1
                    }
                // Uma pizza por mês, do mais recente para o mais antigo.
                val ordered = performance.sortedByDescending { it.period.id }
                Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
                    ordered.chunked(columns).forEach { rowMonths ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                            verticalAlignment = Alignment.Top,
                        ) {
                            rowMonths.forEach { month ->
                                MonthRatingPie(month = month, modifier = Modifier.weight(1f))
                            }
                            repeat(columns - rowMonths.size) {
                                Spacer(modifier = Modifier.weight(1f))
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun MonthRatingPie(
    month: MonthPerformance,
    modifier: Modifier = Modifier,
) {
    val counts = month.counts
    val total = counts.total
    val entries =
        listOf(
            RatingLegendEntry(stringResource(Res.string.operational_satisfaction_col_excellent), counts.excellent, AppPalette.Success500),
            RatingLegendEntry(stringResource(Res.string.operational_satisfaction_col_good), counts.good, AppPalette.Accent500),
            RatingLegendEntry(stringResource(Res.string.operational_satisfaction_col_regular), counts.regular, AppPalette.Warning500),
            RatingLegendEntry(stringResource(Res.string.operational_satisfaction_col_bad), counts.bad, AppPalette.Error500),
        )
    Column(
        modifier = modifier,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space8),
    ) {
        Text(
            text = MonthCalendar.label(month.period),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.onSurface,
            maxLines = 1,
        )
        Box(modifier = Modifier.fillMaxWidth(), contentAlignment = Alignment.Center) {
            if (total == 0) {
                Box(modifier = Modifier.size(DONUT_SIZE).clip(CircleShape).background(AppPalette.Neutral200))
            } else {
                DonutChart(
                    slices =
                        entries.filter { it.value > 0 }.map { entry ->
                            DonutSlice(label = entry.label, value = entry.value.toLong(), color = entry.color)
                        },
                    modifier = Modifier.size(DONUT_SIZE),
                    formatValue = { plainNumber(it) },
                )
            }
        }
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
            entries.forEach { entry ->
                val share = if (total == 0) 0.0 else entry.value.toDouble() / total * 100
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
                ) {
                    Box(modifier = Modifier.size(12.dp).background(entry.color, AppShapes.small))
                    Text(
                        text = entry.label,
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurface,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis,
                        modifier = Modifier.weight(1f),
                    )
                    Text(
                        text = "${entry.value} (${percentText(share)})",
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                    )
                }
            }
        }
    }
}

private data class RatingLegendEntry(
    val label: String,
    val value: Int,
    val color: Color,
)

@Composable
private fun UnitsCard(units: List<UnitSatisfaction>) {
    ChartCard(
        title = stringResource(Res.string.operational_satisfaction_units_title),
        modifier = Modifier.fillMaxWidth(),
    ) {
        if (units.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            Row(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8), verticalAlignment = Alignment.CenterVertically) {
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_location), W_LABEL, TextAlign.Start)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_total_votes), W_SMALL, TextAlign.End)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_excellent), W_SMALL, TextAlign.End)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_good), W_SMALL, TextAlign.End)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_regular), W_SMALL, TextAlign.End)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_bad), W_SMALL, TextAlign.End)
                HeaderCell(stringResource(Res.string.operational_satisfaction_col_satisfaction), W_MEDIUM, TextAlign.End)
            }
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            units.forEachIndexed { index, unit ->
                Row(modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space12), verticalAlignment = Alignment.CenterVertically) {
                    BodyCell(unit.location, W_LABEL, TextAlign.Start)
                    BodyCell(unit.votes.toString(), W_SMALL, TextAlign.End)
                    BodyCell(unit.counts.excellent.toString(), W_SMALL, TextAlign.End)
                    BodyCell(unit.counts.good.toString(), W_SMALL, TextAlign.End)
                    BodyCell(unit.counts.regular.toString(), W_SMALL, TextAlign.End)
                    BodyCell(unit.counts.bad.toString(), W_SMALL, TextAlign.End)
                    BodyCell(
                        text = percentText(unit.satisfactionPercent),
                        weight = W_MEDIUM,
                        align = TextAlign.End,
                        color =
                            if (unit.satisfactionPercent < SATISFACTION_MIN_PERCENT) {
                                AppPalette.Error500
                            } else {
                                MaterialTheme.colorScheme.onSurface
                            },
                    )
                }
                if (index < units.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
        }
    }
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

@Composable
private fun RowScope.BodyCell(
    text: String,
    weight: Float,
    align: TextAlign,
    color: Color = MaterialTheme.colorScheme.onSurface,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        color = color,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space8),
    )
}

private fun axisValue(
    value: Float,
    percent: Boolean,
): String = if (percent) percentText(value.toDouble()) else plainNumber(value.roundToLong())

private fun percentText(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
